import { useEffect, useRef } from 'react'
import { Button } from '~/components/ui/button'
import { Card } from '~/components/ui/card'

// 재사용 확인 다이얼로그. 별도 Dialog 프리미티브가 없어 오버레이 + Card로 구성한다.
// 위험 작업(권한 변경·삭제 등) 적용 전 한 번 더 확인받는 용도. (DESIGN.md §5.2)
interface ConfirmDialogProps {
    open: boolean
    title: string
    description?: string
    confirmLabel?: string
    danger?: boolean
    onConfirm: () => void
    onCancel: () => void
}

export function ConfirmDialog({
    open,
    title,
    description,
    confirmLabel = '확인',
    danger = false,
    onConfirm,
    onCancel,
}: ConfirmDialogProps) {
    const confirmRef = useRef<HTMLButtonElement>(null)

    // 열릴 때 Esc로 취소, 확인 버튼에 포커스를 둔다.
    useEffect(() => {
        if (!open) return
        confirmRef.current?.focus()
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onCancel()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [open, onCancel])

    if (!open) return null

    return (
        <div
            className='fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4'
            onClick={onCancel}>
            <Card
                role='alertdialog'
                aria-modal='true'
                aria-labelledby='confirm-dialog-title'
                aria-describedby={
                    description ? 'confirm-dialog-desc' : undefined
                }
                className='w-full max-w-md p-5 shadow-sm'
                onClick={(e) => e.stopPropagation()}>
                <h2
                    id='confirm-dialog-title'
                    className='text-foreground text-base font-semibold tracking-tight'>
                    {title}
                </h2>
                {description ? (
                    <p
                        id='confirm-dialog-desc'
                        className='text-muted-foreground mt-2 text-[13px]'>
                        {description}
                    </p>
                ) : null}
                <div className='mt-5 flex justify-end gap-2'>
                    <Button variant='outline' size='sm' onClick={onCancel}>
                        취소
                    </Button>
                    <Button
                        ref={confirmRef}
                        variant={danger ? 'destructive' : 'default'}
                        size='sm'
                        onClick={onConfirm}>
                        {confirmLabel}
                    </Button>
                </div>
            </Card>
        </div>
    )
}
